import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  measureFormatter,
  useAnalyticsMeta,
  useFormat,
  type MeasureMeta,
} from '@/Pages/Reports/analytics';
import { useBrandAnalyticsMutation, type BrandDetail } from '../brandsApi';
import { messageOf } from '../lib';

const MAX_MEASURES = 8;

/** A stable reference so `useMemo` below does not see a "new" array every
 *  render while `meta.data` is still loading. */
const NO_MEASURES: MeasureMeta[] = [];

/**
 * ADR-0023 §8 — a consolidated KPI rollup across every branch this brand
 * carries. `brands.analytics`, its own permission distinct from
 * `brands.view` (a franchisor reading a sibling franchisee's revenue through
 * this rollup is a different authority from reading the brand's own standards
 * document).
 *
 * `BrandsService.analytics` is `SemanticQueryService.run()` with the brand's
 * own branch ids filtered in — the SAME engine and the SAME `GET
 * /analytics/meta` catalogue `Pages/Reports/QueryBuilder.tsx` reads, so the
 * measure list here is never a second, hand-typed copy that can drift from
 * the registry. Money renders through `useFormat(null)` deliberately: a
 * brand's branches are not guaranteed to share one currency, and a wrong
 * currency label on a summed figure is worse than an honest "mixed" tag.
 */
export default function AnalyticsPanel({ brand }: { brand: BrandDetail }) {
  const { t } = useTranslation();
  const meta = useAnalyticsMeta();
  const format = useFormat(null);
  const renderMeasure = useMemo(() => measureFormatter(format), [format]);

  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [selected, setSelected] = useState<string[]>([]);

  const [run, { data: result, isLoading: running, error }] = useBrandAnalyticsMutation();

  const measures = meta.data?.measures ?? NO_MEASURES;
  const byName = useMemo(
    () => new Map(measures.map((m) => [m.name, m] as const)),
    [measures],
  );

  const toggle = (measure: MeasureMeta) => {
    if (!measure.permitted) return;
    setSelected((current) =>
      current.includes(measure.name)
        ? current.filter((n) => n !== measure.name)
        : current.length >= MAX_MEASURES
          ? current
          : [...current, measure.name],
    );
  };

  const runQuery = () => {
    if (!selected.length) return;
    void run({ brandId: brand.id, body: { measures: selected, from, to } });
  };

  if (meta.loading) return <LoadingState />;
  if (meta.error) {
    return <ErrorState message={meta.error} onRetry={meta.reload} />;
  }
  if (!brand.branches.length) {
    // Zero branches is a legitimate, distinct empty state — a rollup with
    // nothing to roll up — never "the query failed".
    return (
      <EmptyState
        title={t('brands.analytics.noBranches')}
        hint={t('brands.analytics.noBranchesHint')}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('brands.analytics.title')}</CardTitle>
          <CardDescription>
            {t('brands.analytics.subtitle', { count: brand.branches.length })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {measures.map((measure) => {
              const on = selected.includes(measure.name);
              return (
                <button
                  key={measure.name}
                  type="button"
                  disabled={!measure.permitted}
                  onClick={() => toggle(measure)}
                  title={measure.permitted ? measure.description : t('brands.analytics.notPermitted')}
                  className={
                    'rounded-full border px-3 py-1.5 text-sm ' +
                    (on
                      ? 'border-accent bg-accent-solid text-accent-on'
                      : measure.permitted
                        ? 'hover:bg-hover'
                        : 'cursor-not-allowed opacity-40')
                  }
                >
                  {measure.name}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Field label={t('reports.filters.from')}>
                <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
              </Field>
            </div>
            <div className="w-40">
              <Field label={t('reports.filters.to')}>
                <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
              </Field>
            </div>
            <Button onClick={runQuery} disabled={!selected.length || running}>
              {running ? t('common.loading') : t('brands.analytics.run')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && <ErrorState message={messageOf(error, t('brands.analytics.runFailed'))} onRetry={runQuery} />}

      {result && (
        <Card>
          <CardContent className="p-0">
            {result.rows.length === 0 ? (
              <EmptyState
                title={t('brands.analytics.noData')}
                hint={t('brands.analytics.noDataHint')}
              />
            ) : (
              <TableWrap className="border-0">
                <Table>
                  <THead>
                    <TR>
                      {result.measures.map((name) => (
                        <TH key={name} className="text-end tabular-nums">
                          {name}
                        </TH>
                      ))}
                    </TR>
                  </THead>
                  <tbody>
                    {result.rows.map((row, i) => (
                      <TR key={i}>
                        {result.measures.map((name) => (
                          <TD key={name} className="text-end tabular-nums">
                            {renderMeasure(row[name], byName.get(name))}
                          </TD>
                        ))}
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
